import { useState, useEffect, memo } from 'react';
import {
  useSession,
  useNextgenRouter,
  useToast,
  Theme,
  useTranslation,
  Notification,
  useDeviceTypeMediaQuery,
  AccountType,
} from '@nextgen-web-framework/all';
import {
  profileApi,
  RegFlowConfig,
  RegistrationFlowDataType,
  updateRegistrationData,
  CreateChallengeRequest,
  DynamicObjectInterface,
  RegistrationStatus,
  ProfileMapping,
  ProfileRole,
  updateCustomerType,
  AccountDetailRenewalLifecycleEnum,
  RoutePaths,
} from '@commerce-profile-webcomponents/sdk';
import { useDispatch, useSelector } from 'react-redux';
import { Logger, StorageService, type AppDispatch, type AppState } from '@nextgen-web-framework/core';
import { CoAppCommonProps, CoAppScreenEnum } from '../types/propTypes';
import { getRegistration } from '../../../utils/accountLifeCycleUtils';
import { parseString, parseArray, parseNumber, parseObject, defaultApiErrorHandler } from '../../../utils/helper';
import { useLocale } from '../../../utils/customHooks';
import { useMyAccount } from '../../../utils/myAccountHook';
import RegistrationOTPWrapper from '../../RegistrationOTPWrapper/RegistrationOTPWrapper';
import { fetchAccountTypeImagesCMSIfUndefined, fetchProfileCMSIfUndefined } from '../../../utils/ProfileCMSUtils';
import { MyAccountExtEnum, ToastMessageEnum } from '../../../utils/types/enum';
import { getAge, getExistingPartnerCID } from '../../../utils/registrationUtils';
import { getExt } from '../../../utils/myAccountProfileUtils';
import { ANONYMOUS, COAPP_STEPS, ERROR_GENERIC_MESSAGE, SHARECODE_ATTRIBUTES } from '../../../utils/Constants';
import { useAutoRenewal } from '../../../utils/accountLifeCycleHook';
import { useRegistrationFlowConfig } from '../../../utils/masterdata';
import Registration from '../../Registration/Registration';
import { useGlobalErrorHandler } from '../../../utils/globalErrorHook';
import ApiErrorModal from '../../ApiErrorModal/ApiErrorModal';
import { ApiErrorModalProps } from '../../ApiErrorModal/types/propTypes';
import CoApplicantProfile from '../CoApplicantProfile/CoApplicantProfile';
import CoAppIdStep from '../CoAppIdStep/CoAppIdStep';
import CoAppCommonScreen from '../CoAppCommonScreen/CoAppCommonScreen';
import { APICallType } from '../../Registration/types/propTypes';

const OTPComponent = memo(RegistrationOTPWrapper);
const CoApplicantIdStep = ({ nextHandler, idStepIsLanding }: CoAppCommonProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccountPhone, getAccountEmail } = useAutoRenewal();
  const flowConfigMasterData = useRegistrationFlowConfig();
  const t = useTranslation();
  const { push } = useNextgenRouter();
  const { addToast } = useToast();
  const siteLocale = useLocale();
  const { isMobile } = useDeviceTypeMediaQuery();
  const primaryContactTypeEmail = flowConfigMasterData?.coApplicantFlow?.loggedInUserPrimaryContact === 'EMAIL';

  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const globalApiErrorHandler = useGlobalErrorHandler();
  const registrationData = useSelector(
    (state: AppState) => state.commerceProfile.registrationFlowData,
  ) as RegistrationFlowDataType;
  const { currentStepName, OtpData, userData, redirectUrl } = registrationData;
  const { accountNum: newAccountNumber } = userData;
  const newAccountNum = parseString(newAccountNumber);
  const { session } = useSession();
  const { id, aboNum, type } = session;
  const accountNum = parseString(aboNum);
  const profileId = parseString(id);
  const accountType = parseString(type);

  // Profile Masterdata
  const { getCoAppConfig } = useMyAccount();
  const { ext } = getCoAppConfig();

  // To show profile
  const [showProfile, setShowProfile] = useState<boolean>(false);
  const [screenType, setScreenType] = useState<CoAppScreenEnum>(CoAppScreenEnum.NONE);
  // otpVerify step
  const [showOTP, setShowOTP] = useState<boolean>(false);
  const [challengeId, setChallengeId] = useState('');
  const [username, setUsername] = useState('');
  const [showIdStep, setShowIdStep] = useState<boolean>(false);

  // Pending step
  const [regId, setRegId] = useState<string>('');

  // button status
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  // To handle button message
  const [buttonMessage, setButtonMessage] = useState<string>('');
  const [buttonMessageTheme, setButtonMessageTheme] = useState<Theme>(Theme.BLACK);
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();

  const apiErrorHandler = defaultApiErrorHandler;
  const logger = Logger.getInstance();
  useEffect(() => {
    if (!currentStepName) {
      checkCoAppRegistrationStatus();
    } else if (currentStepName === COAPP_STEPS.P2_ID_STEP) {
      setShowIdStep(true);
    }

    // To get account type from localstorage
    const localstorage = StorageService.getLocalStorage();
    const sharecodeAttributes: DynamicObjectInterface = localstorage?.retrieve?.(SHARECODE_ATTRIBUTES);
    const shareAcctType = sharecodeAttributes?.acctType ?? type ?? AccountType.BusinessOwner;
    // Fallback to ABO if ANONYMOUS
    const accountType = shareAcctType === ANONYMOUS ? AccountType.BusinessOwner : shareAcctType;

    fetchAccountTypeImagesCMSIfUndefined(
      dispatch,
      accountTypeImagesCmsData,
      accountType as AccountType,
      apiErrorHandler,
    );

    dispatch(updateCustomerType(accountType));
  }, []);

  useEffect(() => {
    // To handle drawer & OTP issue
    if (showOTP && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      // enable scroll
      document.body.style.removeProperty('overflow');
    }

    // Cleanup on unmount
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [showOTP]);

  useEffect(() => {
    fetchProfileCMSIfUndefined(dispatch, registrationCmsData, siteLocale, apiErrorHandler);
  }, [siteLocale]);

  const getCoAppAccount = async (accountNum: string) => {
    try {
      const res = await apiErrorHandler(() => profileApi.getAccountByNum(accountNum));
      const { profileList } = res;
      return parseArray(profileList);
    } catch (_) {
      return [];
    }
  };

  const getChallangeId = async (payload: DynamicObjectInterface, regId: string) => {
    const challengeRes = await profileApi.postRegistrationChallenge({
      payload,
      registrationId: regId,
    });
    return challengeRes;
  };

  const checkCoAppRegistrationStatus = async () => {
    // This function to check the current regisration status of the co-applicant
    // Check any pending invitation
    // const isCoappInvited = await getCoAppRegistration(accountNum);
    const filterStatus = getExt(MyAccountExtEnum.CO_APP_INVITE_STATUS, ext) as string;

    const isCoappInvited = await getRegistration(
      RegFlowConfig.CoApplicantFlow,
      accountNum,
      // Fallback to CoapplicantInvited if not defined
      parseString(filterStatus, RegistrationStatus.CoapplicantInvited) as RegistrationStatus,
      true,
      apiErrorHandler,
      true,
    );
    // status = true > co-app registration in progress > go to pending screen
    // status = false > no co-app data. Assume not started or completed* (to clarify after backend completed)
    if (isCoappInvited?.registrationId) {
      // Go to pending screen
      setRegId(isCoappInvited?.registrationId);

      setScreenType(CoAppScreenEnum.PENDING);
    } else {
      const profileList = await getCoAppAccount(accountNum);

      const hasCoApp = profileList?.find(
        (account: ProfileMapping) => !account.primary && account.roleCode === ProfileRole.Owner,
      );

      if (hasCoApp) {
        // Go to profile screen
        dispatch(
          updateRegistrationData({
            ...registrationData,
            userData: {
              ...registrationData.userData,
              partnerAccounts: profileList,
            },
          }),
        );
        setShowProfile(true);
      } else {
        // New Co-App Registration and check status
        await precheckCoAppRegistration();
      }
    }
  };

  const generalErrorButtonMessage = (message: string) => {
    setButtonMessage(message);
    setButtonMessageTheme(Theme.ERROR);
    setDisableButton(true);
    setIsButtonLoading(false);
  };

  const precheckCoAppRegistration = async () => {
    // Check for businessNatureLifeCycle
    const accountRes = await apiErrorHandler(() => profileApi.getAccountInformation(accountNum));
    const { businessNatureLifeCycle, initialLifeCycle } = accountRes;
    if (
      businessNatureLifeCycle === AccountDetailRenewalLifecycleEnum.Processing ||
      initialLifeCycle !== AccountDetailRenewalLifecycleEnum.Completed
    ) {
      setDisableButton(true);
    }

    // Check for age
    const newCoAppConfig = getExt(MyAccountExtEnum.CO_APP_NEW_CONFIG, ext) as DynamicObjectInterface;
    const { minAge: minAgeConfig } = parseObject(newCoAppConfig?.[accountType]);
    const { value: minAge = 0, ageCalAtyearEnd = false } = parseObject(minAgeConfig);

    let isValidAge = true;
    if (minAge) {
      const profile = await apiErrorHandler(() => profileApi.getProfilesById(profileId));
      const { birthDate } = profile;
      const age = Math.abs(getAge(String(birthDate), false, ageCalAtyearEnd, 'YYYY-MM-DD'));
      isValidAge = age >= minAge;
    }

    if (!isValidAge) {
      // Age is less than minimum age
      generalErrorButtonMessage(t('profile.coApp.common.ageNotValid', { minAge }));
    }

    setScreenType(CoAppScreenEnum.OTP_VERIFY);
  };

  const requestOtpProcess = async () => {
    // Get Primary account profile
    const profile = primaryContactTypeEmail ? await getAccountEmail() : await getAccountPhone();
    if (profile?.phone || profile?.email) {
      const contactInfo = primaryContactTypeEmail ? profile?.email : profile?.phone;
      const contact = parseString(contactInfo);

      const updatedData = {
        ...registrationData,
        OtpData: {
          ...registrationData.OtpData,
          inputType: primaryContactTypeEmail ? 'email' : 'phone',
        },
        userData: {
          ...registrationData.userData,
          jwtToken: null,
        },
      };

      try {
        if (contact) {
          setUsername(contact);
          // Seq #22 - get registrationId
          const { registrationId, version } = await globalApiErrorHandler(() =>
            profileApi.createRegistrationId(
              {
                registrationFlow: RegFlowConfig.CoApplicantFlow,
                // not require returnUrl here
              },
              {},
              true,
            ),
          );

          updatedData.userData.registrationId = parseString(registrationId);
          updatedData.userData.patchVersion = parseNumber(version);
        }

        if (updatedData.userData.registrationId) {
          // Seq #28 - get challenges
          const payload: CreateChallengeRequest = {
            [updatedData.OtpData.inputType]: contact,
          };

          const challengeRes = await globalApiErrorHandler(() =>
            getChallangeId(payload, updatedData.userData.registrationId ?? ''),
          );
          if (challengeRes?.challengeId) {
            setChallengeId(parseString(challengeRes?.challengeId));
            updatedData.userData.patchVersion = challengeRes?.version;
            setShowOTP(true);
            dispatch(updateRegistrationData(updatedData));
          }
        }
      } catch (error) {
        logger.log(error, 'error');
        setModalErrorData({
          headline: ERROR_GENERIC_MESSAGE.headline,
          body: t('profile.common.errorHeading'),
        });
      }
    }
  };

  const handleOTPStep = async () => {
    if (!idStepIsLanding) {
      const partnerCID = await getExistingPartnerCID(accountNum);
      // To handle for OTP verification here
      const regData = {
        ...registrationData,
        OtpData: {
          ...OtpData,
          challengeId: challengeId,
          isOTPVerified: true,
        },
        cidVerification: {
          ...registrationData.cidVerification,
          spousePersonalId: partnerCID,
        },
      };
      await Promise.resolve(nextHandler?.(null, regData, true, false, APICallType.SKIP));
    }
    logger.log('otp verified handler');
    setShowOTP(false);
    setShowIdStep(true);
  };

  const resendInvitation = async () => {
    const regRes = await apiErrorHandler(() => profileApi.getRegistrationDetailsById(regId));

    const { version } = parseObject(regRes);

    const res = await apiErrorHandler(() =>
      profileApi.patchRegistration(
        {
          version,
        },
        regId,
        idStepIsLanding ? 'inviteStep' : 'coApplicantReInviteStep',
      ),
    );
    if (res) {
      addToast({
        id: ToastMessageEnum.INFORMATION_SAVED,
        timeout: 3000,
        content: (
          <Notification
            hasNotificationIcon={true}
            theme={Theme.SUCCESS}
            message={t('profile.coApp.common.requestSent')}
          />
        ),
      });
      setDisableButton(true);

      setTimeout(() => {
        setDisableButton(false);
      }, 3000);
    } else {
      addToast({
        id: ToastMessageEnum.INFORMATION_SAVED,
        timeout: 3000,
        content: (
          <Notification
            hasNotificationIcon={true}
            theme={Theme.ERROR}
            message={t('profile.ApiErrorModal.genericError.body')}
          />
        ),
      });
    }
    setIsButtonLoading(false);
  };

  const handleButton = async () => {
    setIsButtonLoading(true);
    if (screenType === CoAppScreenEnum.PENDING) {
      await resendInvitation();
    } else if (currentStepName === '' && profileId) {
      await requestOtpProcess();
    } else if (newAccountNum && redirectUrl) {
      // Go to login screen
      push?.(redirectUrl);
    }
    // Only 2 cases
  };

  const getComponent = () => {
    if (showProfile) {
      return <CoApplicantProfile />;
    } else if (idStepIsLanding && showIdStep) {
      return (
        <Registration
          flowName={RegFlowConfig.CoApplicantFlow}
          sourceUrl={''}
          onSuccess={() => {
            logger.log('success');
          }}
          redirectUrl={RoutePaths.DASHBOARD}
          registrationId={registrationData?.userData?.registrationId}
          idStepIsLanding={idStepIsLanding}
        />
      );
    } else if (showIdStep) {
      return <CoAppIdStep nextHandler={nextHandler} />;
    } else {
      // Here will show all common design screens
      return (
        <CoAppCommonScreen
          buttonHandler={handleButton}
          screenType={screenType}
          buttonLoading={isButtonLoading}
          disableButton={disableButton}
          buttonMessage={buttonMessage}
          buttonMessageTheme={buttonMessageTheme}
        />
      );
    }
  };

  const handleOTPDrawerClose = () => {
    setIsButtonLoading(false);
    setShowOTP(false);
  };

  return (
    <>
      {getComponent()}
      {showOTP && (
        <OTPComponent
          username={username}
          challengeId={challengeId}
          nextHandler={handleOTPStep}
          onCloseDrawer={handleOTPDrawerClose}
          showChangeCTA={false}
        />
      )}
      {<ApiErrorModal {...modalErrorData} />}
    </>
  );
};

export default CoApplicantIdStep;
